/**
 * What has been read goes into the store; what the store holds is no longer claimed to be everything.
 *
 * The console's screens, its helpers and its command layer all address records through the store: a
 * revoke dialog finds its record there, the detail head resolves a reissue chain's neighbours there,
 * and `commands-api` turns a public code into an id there. That is worth keeping — it is one place,
 * and it is what makes the demo and the product run the same screens.
 *
 * What is not worth keeping is the store pretending to be the whole world. So the reads that page
 * (`V07Queries`) are the authority on totals and on which records exist, and they ADOPT what they
 * fetched into the store on the way past, so everything downstream keeps working on records that are
 * actually on screen.
 *
 * Adoption is ONCE PER ANSWER, and that is the whole safety argument.
 *
 * A store write clones the store, which re-renders the screen, which runs this effect again. The
 * first version of this file keyed the effect on the array it was handed — and a screen that builds
 * `[fetched]` inline hands it a new array every render, so the same record was written, re-rendered,
 * written again. An independent run measured thirteen attempts on one detail screen before the test
 * harness stopped counting. Keying on the array's CONTENTS is not enough either: after a single-record
 * read is hydrated with its claims, its revisions and its ledger, the store's copy is deliberately a
 * different and richer object than the one adopted, and an effect that kept the store equal to the
 * read would overwrite the rich record with the lean one on the next render.
 *
 * So each answer object is adopted at most once, tracked by identity in a `WeakSet`. A new answer —
 * a new page, a re-read after a command — is a new object and is adopted. The same answer, handed
 * back on a later render in a new array, is not.
 */
import { useEffect, useRef } from 'react';
import { useDemoUpdate } from './store';
import type { DemoBatch, DemoCredential } from './types';

/** One answer, adopted once. Re-handed on a later render, it is already known and nothing is written. */
function useAdoptOnce<T extends object>(
  rows: readonly T[] | null,
  write: (db: import('./types').DemoDatabase, fresh: T[]) => void,
): void {
  const update = useDemoUpdate();
  const adopted = useRef<WeakSet<object>>(new WeakSet());
  useEffect(() => {
    if (rows === null || rows.length === 0) return;
    const fresh = rows.filter((row) => !adopted.current.has(row));
    if (fresh.length === 0) return;
    for (const row of fresh) adopted.current.add(row);
    update((db) => write(db, fresh));
  }, [rows, update, write]);
}

export function useAdoptCredentials(rows: readonly DemoCredential[] | null): void {
  useAdoptOnce(rows, writeCredentials);
}

function writeCredentials(db: import('./types').DemoDatabase, fresh: DemoCredential[]): void {
  for (const row of fresh) {
    const at = db.credentials.findIndex((c) => c.id === row.id);
    if (at < 0) db.credentials.push(row);
    else db.credentials[at] = row;
  }
}

export function useAdoptBatches(rows: readonly DemoBatch[] | null): void {
  useAdoptOnce(rows, writeBatches);
}

/**
 * The batch, without the window it was read through.
 *
 * `rowPage`, `rowsOffset` and `rowsTotal` describe ONE answer — which slice was asked for — and the
 * store is not a record of answers, it is the world the screens read. Writing them would leave the
 * previous page's rows in the store to be drawn for a frame under the next page's number, and the
 * demo's commands would be walking a window instead of a batch.
 */
function sourceOf(batch: DemoBatch): DemoBatch {
  if (batch.rowPage === undefined && batch.rowsOffset === undefined && batch.rowsTotal === undefined) {
    return batch;
  }
  const { rowPage: _page, rowsOffset: _offset, rowsTotal: _total, ...source } = batch;
  return source as DemoBatch;
}

function writeBatches(db: import('./types').DemoDatabase, fresh: DemoBatch[]): void {
  for (const row of fresh) {
    const source = sourceOf(row);
    const at = db.batches.findIndex((b) => b.id === source.id);
    if (at < 0) db.batches.push(source);
    else db.batches[at] = source;
  }
}

/**
 * The server's per-template credential counts.
 *
 * Adopted rather than threaded through six call sites, because `tplCount` is asked from the template
 * list, the editor's publish gate and two archive dialogs, and all four have to agree.
 *
 * Compared by VALUE, not by identity: every read of the summary builds a fresh object with the same
 * numbers in it, and writing that on each render would be the same loop as above with one more step.
 */
export function useAdoptTemplateCounts(counts: Record<string, number> | null): void {
  const update = useDemoUpdate();
  const written = useRef<Record<string, number> | null>(null);
  useEffect(() => {
    if (counts === null) return;
    if (written.current !== null && sameCounts(written.current, counts)) return;
    written.current = counts;
    update((db) => {
      db.credentialCountByTemplate = counts;
    });
  }, [counts, update]);
}

function sameCounts(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const k of keys) if (a[k] !== b[k]) return false;
  return true;
}
